import { RefObject, useMemo, useState } from 'react'
import {
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormReset,
  UseFormReturn,
  UseFormSetValue,
} from 'react-hook-form'
import { t } from 'i18next'
import isEqual from 'lodash/isEqual'
import { ConfiguredColumn, ObjectFieldDTO, ShortDisplayRule } from 'src/types'
import { GETEntityFilter } from 'src/types/api/container.api'
import { v4 as uuid } from 'uuid'
import { AutocompleteOption } from '@microservices/wiskey-react-components/dist/AutoComplete'

import { EmbeddedObjectShort } from '@pages/EntityCreateOrEdit/types'
import { FieldForm } from '@pages/FormCreateOrEdit/components/AddFieldDialog/helpers/getDefaultValue'
import {
  getInitialPropertiesForRequest,
  getPropertiesForUpdate,
} from '@pages/Parameters/helpers/getPropertiesForUpdate'

import {
  ExternalTextSettings,
  GetTextParamsHandle,
  TextParams,
  TextSettingsStateType,
} from '@components/TextSettings'

import { useFetchAllDisplayRulesQuery } from '@redux/api'
import { useUpdateLocalParameterPropertyMutation } from '@redux/api/parameters.api'

import {
  getColumnParametersFromArray,
  getParametersForCreation,
  mapValueTypeToValueFormat,
} from '@helpers'
import {
  BIND_TYPE,
  DEFAULT_GLOBAL_COLUMN_HEADER_PARAMETERS,
  DEFAULT_GLOBAL_COLUMN_TEXT_PARAMETERS,
  DEFAULT_INITIAL_STATE_COLUMN_HEADER_PARAMETERS,
  DEFAULT_INITIAL_STATE_COLUMN_TEXT_PARAMETERS,
  DROPDOWN_FILTER_TYPES,
  MODAL_TYPE,
  MODEL_TYPE,
  NAMES_FIELDS,
  PREFILL_OPTIONS,
} from '@constants'
import { GETDropDownFilter } from '@types'

import { AddColumnDialogType } from '../AddColumnDialog'
import { getDefaultValue } from '../helpers'

import { ColumnForm } from './useInputs'

type ColumnSuccessCallbackParams = {
  columnData: ConfiguredColumn
  columnHeaderData: TextParams | undefined
  columnTextData: TextParams | undefined
}

type useHandlersParams = {
  objectField: ObjectFieldDTO | undefined
  watchBinding?: BIND_TYPE
  watchValue: string | null
  watchUseParameters: boolean
  setValue: UseFormSetValue<ColumnForm>
  getValues: UseFormGetValues<ColumnForm>
  isDirty: boolean
  watchPathArray: EmbeddedObjectShort[]
  isObjectLinkedValue: boolean
  reset: UseFormReset<ColumnForm>
  handleSubmit: UseFormHandleSubmit<ColumnForm>
  viewObjectCode: string
  columnHeaderSettingsRef: RefObject<GetTextParamsHandle>
  columnTextSettingsRef: RefObject<GetTextParamsHandle>
  initialColumnHeaderStyles: TextSettingsStateType | null
  initialColumnTextStyles: TextSettingsStateType | null
  watchUseDisplayRule?: boolean
  watchDisplayRule?: AutocompleteOption<string | number> | null | undefined
  watchDisableEditable?: boolean
  watchActions?: AutocompleteOption[]
  watchedDropDownListFilters: GETDropDownFilter[]
  watchedDropDownWindowFilters: GETDropDownFilter[]
  methods: UseFormReturn<FieldForm, any, undefined>
  contextProps: AddColumnDialogType
}

export const useHandlers = ({
  objectField,
  watchBinding,
  watchUseParameters,
  watchPathArray,
  setValue,
  getValues,
  watchValue,
  isDirty,
  isObjectLinkedValue,
  reset,
  handleSubmit,
  viewObjectCode,
  columnHeaderSettingsRef,
  columnTextSettingsRef,
  initialColumnHeaderStyles,
  initialColumnTextStyles,
  watchUseDisplayRule,
  watchDisplayRule,
  watchDisableEditable,
  watchActions,
  watchedDropDownListFilters,
  watchedDropDownWindowFilters,
  contextProps,
}: useHandlersParams) => {
  const {
    onCloseModal,
    currentRow,
    modalType,
    rawObjectFields,
    entityCode,
    currentEntity,
    onCreateColumn,
    onUpdateColumn,
    type,
  } = contextProps

  const defaultValues: ColumnForm = getDefaultValue({ type })

  const [isDirtyHeader, setDirtyHeader] = useState(false)
  const [isDirtyText, setDirtyText] = useState(false)
  const [isShowScriptValueDialog, setScriptValueDialog] = useState(false)
  const [isShowResetConfirmModal, setShowResetConfirmModal] = useState(false)

  const [isShowFilterDialog, setShowFilterDialog] = useState(false)
  const [currentTypeFilter, setCurrentTypeFilter] = useState<DROPDOWN_FILTER_TYPES | null>(null)
  const [currentFilter, setCurrentFilter] = useState<GETEntityFilter | null>(null)

  const viewType = currentEntity?.type

  const actionIds = watchActions?.map(action => action.id)

  const [updateParameterProperty] = useUpdateLocalParameterPropertyMutation()

  const { data: displayRulesData, isFetching: isLoadingDisplayRules } =
    useFetchAllDisplayRulesQuery(
      {
        objectCode: currentRow?.objectCode ? currentRow?.objectCode : viewObjectCode,
        objectField:
          currentRow?.bindType === BIND_TYPE.FIELD && currentRow?.value
            ? currentRow.value
            : undefined,
      },
      {
        skip: !watchUseDisplayRule,
        refetchOnMountOrArgChange: true,
      }
    )

  const autocompleteOptionsDisplayRules = useMemo(() => {
    if (displayRulesData) {
      return (displayRulesData as unknown as ShortDisplayRule[]).map(displayRule => ({
        id: displayRule.id,
        label: displayRule.code,
      }))
    }
  }, [displayRulesData])

  const handleDoubleClickChip = (filter: GETEntityFilter, type: DROPDOWN_FILTER_TYPES) => {
    setCurrentFilter(filter)
    setCurrentTypeFilter(type)
    setShowFilterDialog(true)
  }

  const handleAddFilter = (type: DROPDOWN_FILTER_TYPES) => {
    setShowFilterDialog(true)
    setCurrentTypeFilter(type)
  }

  const handleResetFilterDialog = () => {
    setShowFilterDialog(false)
    setCurrentFilter(null)
    setCurrentTypeFilter(null)
  }

  const handleDeleteFilter = (filter: GETEntityFilter, type: DROPDOWN_FILTER_TYPES) => {
    if (type === DROPDOWN_FILTER_TYPES.LIST) {
      setValue(
        'dropDownListFilters',
        watchedDropDownListFilters.filter(({ id, internal_id }) =>
          filter.id ? id !== filter.id : filter.internal_id != internal_id
        ),
        { shouldDirty: true }
      )
    }
    if (type === DROPDOWN_FILTER_TYPES.WINDOW) {
      setValue(
        'dropDownWindowFilters',
        watchedDropDownWindowFilters.filter(({ id, internal_id }) =>
          filter.id ? id !== filter.id : filter.internal_id != internal_id
        ),
        { shouldDirty: true }
      )
    }
  }

  const handleSubmitFilter = (filter: GETEntityFilter) => {
    if (currentTypeFilter) {
      const newFilter = {
        ...filter,
        type: currentTypeFilter,
        internal_id: filter.id ? undefined : filter.internal_id || uuid(),
      }

      const isList = currentTypeFilter === DROPDOWN_FILTER_TYPES.LIST
      const inputName = isList ? 'dropDownListFilters' : 'dropDownWindowFilters'
      const watchedFilters = isList ? watchedDropDownListFilters : watchedDropDownWindowFilters
      const updatedFilters = watchedFilters.map(item => {
        if (
          (filter.id && item.id === filter.id) ||
          (filter.internal_id && item.internal_id === filter.internal_id)
        ) {
          return newFilter
        }

        return item
      })

      if (filter.id || filter.internal_id) {
        setValue(inputName, updatedFilters, { shouldDirty: true })
        handleResetFilterDialog()

        return
      }

      setValue(inputName, [...watchedFilters, newFilter], { shouldDirty: true })
      handleResetFilterDialog()
    }
  }

  const handleCloseFilterDialog = (isDirty: boolean) => {
    if (isDirty && !confirm(t('notifications.leave'))) {
      return
    }

    handleResetFilterDialog()
  }

  const handleTextExternalDirty = (object: ExternalTextSettings) => {
    if (object && initialColumnTextStyles) {
      const isDirty = !isEqual(object, initialColumnTextStyles)

      setDirtyText(isDirty)
    }
  }

  const handleHeaderExternalDirty = (object: ExternalTextSettings) => {
    if (object && initialColumnTextStyles) {
      const isDirty = !isEqual(object, initialColumnHeaderStyles)

      setDirtyHeader(isDirty)
    }
  }

  const handleChangeValue = (value: string | number) => {
    const objectFieldValue = rawObjectFields?.find(obj => obj.name === value)

    setValue('pathArray', [])
    setValue('objectValue', '')

    if (watchBinding === BIND_TYPE.FIELD && objectFieldValue) {
      const selectValue = { id: 0, label: objectFieldValue.name }
      setValue('columnToSort', selectValue, { shouldDirty: true })
    }
  }

  const toggleOpenScriptValueDialog = (open: boolean) => setScriptValueDialog(open)

  const handleChangeBinding = (value: string | number) => {
    const currentValues = getValues()

    reset({
      ...currentValues,
      value: '',
      preFillLinkStr: '',
      preFillType: undefined,
      preFillStaticValue: '',
      preFillFilterNameStr: '',
      pathArray: [],
    })

    if ((modalType !== MODAL_TYPE.EDIT && !isDirty) || (value && value === BIND_TYPE.FIELD)) {
      setValue('columnToSort', null)
    }
  }

  const handleCreateColumnSuccess = ({
    columnHeaderData,
    columnTextData,
    columnData,
  }: ColumnSuccessCallbackParams) => {
    if (!columnHeaderData || !columnTextData || !columnData?.parameters) {
      return
    }

    const { headerParamId, textParamId } = getColumnParametersFromArray(columnData.parameters)

    if (!watchUseParameters || !textParamId || !headerParamId) {
      return
    }

    const propertiesForCreation = [
      ...getInitialPropertiesForRequest({
        textData: columnHeaderData,
        paramId: headerParamId,
      }),
      ...getInitialPropertiesForRequest({
        textData: columnTextData,
        paramId: textParamId,
      }),
    ]

    updateParameterProperty(propertiesForCreation)
  }

  const handleUpdateColumnSuccess = ({
    columnData,
    columnHeaderData,
    columnTextData,
  }: ColumnSuccessCallbackParams) => {
    if (!columnHeaderData || !columnTextData || !columnData?.parameters) {
      return
    }

    const { headerProperties, textProperties, headerParamId, textParamId } =
      getColumnParametersFromArray(columnData.parameters)

    if (!headerProperties || !textProperties || !headerParamId || !textParamId) {
      return
    }

    if (headerProperties.length === 0 && textProperties.length === 0) {
      handleCreateColumnSuccess({
        columnHeaderData,
        columnTextData,
        columnData,
      })
    }

    const propertiesForUpdate = [
      ...getPropertiesForUpdate(columnHeaderData, headerProperties, headerParamId),
      ...getPropertiesForUpdate(columnTextData, textProperties, textParamId),
    ]

    updateParameterProperty(propertiesForUpdate)
  }

  const handleSave = (
    columnHeaderData: TextParams | undefined,
    columnTextData: TextParams | undefined
  ) => {
    handleSubmit((data: ColumnForm) => {
      const {
        id = Date.now(),
        code,
        bindType = BIND_TYPE.FIELD,
        title,
        value,
        columnToSort,
        objectValue,
        pinnedColumn: _pinnedColumn,
        enumDescription,
        useDisplayRule,
        displayRuleId,
        preFillType,
        preFillLink,
        preFillLinkStr,
        preFillFilterName,
        preFillFilterNameStr,
        preFillStaticValue,
        dropDownListCode,
        dropDownWindowCode,
        dropDownListFilters,
        dropDownWindowFilters,
        asCheckbox,
        asDuration,
        formatDate,
      } = data
      const columnHeaderCode = `column_header_styles_${code}`
      const columnTextCode = `column_text_styles_${code}`

      const valueFormat = {
        valueFormat: objectField?.valueType
          ? mapValueTypeToValueFormat(objectField?.valueType, asCheckbox, asDuration)
          : null,
      }

      const requestData = {
        code,
        bindType,
        title,
        value,
        objectValue: isObjectLinkedValue ? objectValue : undefined,
        pathArray: watchPathArray,
        editable:
          watchBinding === BIND_TYPE.FIELD && watchValue !== NAMES_FIELDS._ID
            ? !watchDisableEditable
            : false,
        dropDownListCode:
          typeof dropDownListCode === 'object' && dropDownListCode?.id
            ? (dropDownListCode?.id as string)
            : '',
        dropDownWindowCode:
          typeof dropDownWindowCode === 'object' && dropDownWindowCode?.id
            ? (dropDownWindowCode?.id as string)
            : '',
        dropDownListFilters: dropDownListFilters || [],
        dropDownWindowFilters: dropDownWindowFilters || [],
        preFill: preFillType
          ? {
              preFillType: preFillType.option,
              preFillFilterName:
                preFillType.option === PREFILL_OPTIONS.STATIC ? preFillFilterName : null,
              preFillFilterNameStr:
                preFillType.option === PREFILL_OPTIONS.STATIC ? preFillFilterNameStr : null,
              value: preFillType.option === PREFILL_OPTIONS.STATIC ? preFillStaticValue : null,
              preFillLink: preFillLinkStr ? preFillLink : null,
              preFillLinkStr,
            }
          : null,
        viewCode: entityCode,
        viewType,
        objectCode: viewObjectCode,
        columnToSort: columnToSort?.label,
        useLocalParameters: watchUseParameters,
        enumDescription,
        actionIds,
        useDisplayRule,
        displayRuleId: watchDisplayRule?.id,
        formatDate: formatDate ? formatDate.label : null,
        ...valueFormat,
      }

      if (modalType === MODAL_TYPE.EDIT && currentRow) {
        onUpdateColumn({
          ...requestData,
          id,
          // Если решаем использовать параметры, но их на данный момент нет, то создаем их
          ...(watchUseParameters && (!currentRow.parameters.length || type !== MODEL_TYPE.VIEW)
            ? {
                parameters: getParametersForCreation(
                  columnHeaderCode,
                  columnTextCode,
                  columnHeaderData,
                  columnTextData,
                  type
                ),
              }
            : {}),
        }).then(columnData => {
          handleUpdateColumnSuccess({
            columnData,
            columnHeaderData,
            columnTextData,
          })

          onCloseModal()
        })

        return
      }

      onCreateColumn({
        ...requestData,
        parameters: watchUseParameters
          ? getParametersForCreation(
              columnHeaderCode,
              columnTextCode,
              columnHeaderData,
              columnTextData,
              type
            )
          : [],
      }).then(columnData => {
        handleCreateColumnSuccess({
          columnData,
          columnHeaderData,
          columnTextData,
        })

        reset(defaultValues)
        onCloseModal()
      })
    })()
  }

  const handleSetValuesColumnStyles = (
    columnHeaderStyles: TextSettingsStateType,
    columnTextStyles: TextSettingsStateType
  ) => {
    columnHeaderSettingsRef.current?.setValues(columnHeaderStyles)
    columnTextSettingsRef.current?.setValues(columnTextStyles)
  }

  const handleResetParams = () => {
    handleSetValuesColumnStyles(
      DEFAULT_INITIAL_STATE_COLUMN_HEADER_PARAMETERS,
      DEFAULT_INITIAL_STATE_COLUMN_TEXT_PARAMETERS
    )
    setShowResetConfirmModal(false)
  }

  const handleClickReset = () => {
    const columnHeaderData = columnHeaderSettingsRef.current?.getTextParams()
    const columnTextData = columnTextSettingsRef.current?.getTextParams()

    if (
      isEqual(columnHeaderData, DEFAULT_GLOBAL_COLUMN_HEADER_PARAMETERS) &&
      isEqual(columnTextData, DEFAULT_GLOBAL_COLUMN_TEXT_PARAMETERS)
    ) {
      return
    }

    setShowResetConfirmModal(true)
  }

  const handleSetShowResetConfirmModal = (value: boolean) => setShowResetConfirmModal(value)

  return {
    data: {
      autocompleteOptionsDisplayRules,
      isLoadingDisplayRules,
    },
    state: {
      isShowScriptValueDialog,
      isTextParamsDirty: isDirtyHeader || isDirtyText,
      isShowResetConfirmModal,
      isShowFilterDialog,
      currentFilter,
      currentTypeFilter,
    },
    handlers: {
      handleChangeValue,
      handleChangeBinding,
      toggleOpenScriptValueDialog,
      handleSave,
      handleTextExternalDirty,
      handleHeaderExternalDirty,
      handleSetValuesColumnStyles,
      handleSetShowResetConfirmModal,
      handleResetParams,
      handleClickReset,
      handleAddFilter,
      handleDoubleClickChip,
      handleSubmitFilter,
      handleDeleteFilter,
      handleCloseFilterDialog,
    },
  }
}
